import { MapPin, Phone, MessageCircle, Mail, Clock } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { EnquiryForm } from "../components/EnquiryForm";
import { MapBlock } from "../components/MapBlock";
import { Placeholder } from "../components/Placeholder";
import { site } from "../config/siteConfig";

const contactCards = [
  {
    icon: MapPin,
    label: "Visit Us",
    content: (
      <address className="not-italic text-sm leading-relaxed text-brand-muted">
        {site.addressLine1}, {site.addressLine2}, {site.addressLine3}
      </address>
    ),
  },
  {
    icon: Phone,
    label: "Call Us",
    content: (
      <a
        href={site.phoneHref}
        data-testid="contact-phone-link"
        className="text-sm font-semibold text-brand transition-colors hover:text-brand-dark"
      >
        {site.phoneDisplay}
      </a>
    ),
  },
  {
    icon: MessageCircle,
    label: "WhatsApp",
    content: (
      <a
        href={site.whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="contact-whatsapp-link"
        className="text-sm font-semibold text-brand-green transition-colors hover:text-green-700"
      >
        {site.whatsappDisplay}
      </a>
    ),
  },
  {
    icon: Mail,
    label: "Email",
    content: <Placeholder>{site.email}</Placeholder>,
  },
  {
    icon: Clock,
    label: "Opening Hours",
    content: <Placeholder>{site.openingHours}</Placeholder>,
  },
];

export default function ContactPage() {
  return (
    <>
      <Seo
        title="Contact | Ansar Public School Mundeke Goraya, Daska"
        description="Contact Ansar Public School Mundeke — call +92 345 6232508, send a WhatsApp message, get directions or submit an admission enquiry."
      />
      <PageHero
        chapter="01"
        eyebrow="Contact"
        title="Get in Touch With the School"
        description="Call, WhatsApp, visit the campus in Mundeke Goraya or send an enquiry using the form below."
        crumb="Contact"
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="grid gap-10 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-2">
            {contactCards.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.06}>
                <div
                  data-testid={`contact-card-${i}`}
                  className="flex items-start gap-4 rounded-3xl border border-brand-line bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-light">
                    <c.icon className="h-5 w-5 text-brand" aria-hidden="true" />
                  </span>
                  <div>
                    <h2 className="font-display text-base font-bold text-brand-ink">{c.label}</h2>
                    <div className="mt-1">{c.content}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="lg:col-span-3">
            <div
              data-testid="contact-form-card"
              className="rounded-[2rem] border border-brand-line bg-white p-6 shadow-xl sm:p-9"
            >
              <h2 className="font-display text-2xl font-bold text-brand-ink">
                Send an Admission Enquiry
              </h2>
              <p className="mb-7 mt-2 text-sm leading-relaxed text-brand-muted">
                Complete the form and the school team will contact you using the details
                provided.
              </p>
              <EnquiryForm source="contact" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
          <Reveal>
            <SectionHeading
              chapter="02"
              eyebrow="Location"
              title="Find Ansar Public School"
              description={`${site.addressLine1}, ${site.addressLine2}, ${site.addressLine3}`}
            />
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <MapBlock />
          </Reveal>
        </div>
      </section>
    </>
  );
}
